import { create } from 'zustand';
import { tryParseJwtClaims } from '../core/jwt.js';

export interface PermissionsState {
  permissions: Set<string>;
  roles: string[];
  tenantId: string | null;
  setFromJwt: (jwt: string | null) => void;
  setPermissions: (permissions: Iterable<string>) => void;
  setRoles: (roles: string[]) => void;
  setTenantId: (id: string | null) => void;
  clear: () => void;
  has: (permission: string) => boolean;
  hasAny: (...perms: string[]) => boolean;
  hasAll: (...perms: string[]) => boolean;
}

export const usePermissionsStore = create<PermissionsState>((set, get) => ({
  permissions: new Set<string>(),
  roles: [],
  tenantId: null,
  setFromJwt: (jwt) => {
    if (!jwt) {
      set({ permissions: new Set(), roles: [], tenantId: null });
      return;
    }
    const claims = tryParseJwtClaims(jwt);
    if (!claims) return;
    const rawPerms = claims.permissions;
    const rawRoles = claims.role ?? claims.roles;
    const tenant = (claims.tenant_id ?? claims.tid ?? null) as string | null;
    const permissions = new Set<string>(Array.isArray(rawPerms) ? (rawPerms as string[]) : []);
    const roles = Array.isArray(rawRoles)
      ? (rawRoles as string[])
      : typeof rawRoles === 'string'
        ? [rawRoles]
        : [];
    set({ permissions, roles, tenantId: tenant });
  },
  setPermissions: (perms) => set({ permissions: new Set(perms) }),
  setRoles: (roles) => set({ roles }),
  setTenantId: (tenantId) => set({ tenantId }),
  clear: () => set({ permissions: new Set(), roles: [], tenantId: null }),
  has: (permission) => get().permissions.has(permission),
  hasAny: (...perms) => {
    const set = get().permissions;
    return perms.some((p) => set.has(p));
  },
  hasAll: (...perms) => {
    const set = get().permissions;
    return perms.every((p) => set.has(p));
  }
}));
